import { Switch, Route, Redirect, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { safePostAuthRedirect, signInUrlForLocation } from "./lib/authRedirect";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/not-found";
import { isApplicationRoutePath } from "@/lib/applicationRoutePaths";
import { ThemeProvider, useTheme } from "@/lib/ThemeContext";
import { AccentColorSync } from "@/lib/AccentColorSync";
import { lazy, Suspense, useState, useEffect, useRef } from "react";
import { Loader2 } from "lucide-react";
import { SignIn, SignUp } from "@clerk/react";
import PurchaseCompletePage from "./pages/PurchaseCompletePage";
import { E2E_AUTH_BYPASS, useAppAuth } from "@/lib/useAppAuth";
import { setClerkGetToken } from "@/lib/api";
import { useShortcutsOverlay } from "@/lib/use-shortcuts-overlay";
import { useAccessTier } from "@/hooks/use-access-tier";
import {
  installEffectiveWorkspaceIsolation,
  isolateBrowserForIdentity,
} from "@/lib/tenantIsolation";
import { SettingsHubProvider } from "@/components/settings/SettingsHubProvider";
import { SettingsHub } from "@/components/settings/SettingsHub";
import { PAGE_FRAME } from "@/lib/layout";
import MessagingRoute from "@/components/MessagingRoute";
import { isLegacySessionsTab, sessionListHref } from "@/lib/sessionListNavigation";

const ResultsPage = lazy(() => import("@/pages/ResultsPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const VaultTemplatePage = lazy(() => import("@/vault-template/VaultTemplatePage"));
const BlogManager = lazy(() => import("@/components/blog/BlogManager"));
const ReceptionistEmbedPage = lazy(() =>
  import("@/vault-template/VaultTemplatePage").then((m) => ({
    default: m.ReceptionistEmbedPage,
  })),
);
const LeadCapturePreview = lazy(() => import("@/pages/LeadCapturePreview"));
const OnboardingLeadCapturePreview = lazy(
  () => import("@/pages/OnboardingLeadCapturePreview"),
);
const WebsiteContactLeadCapturePreview = lazy(
  () => import("@/pages/WebsiteContactLeadCapturePreview"),
);
const VaultCampaignLeadCapturePreview = lazy(
  () => import("@/pages/VaultCampaignLeadCapturePreview"),
);
const ProfessionalVariantPreview = lazy(
  () => import("@/pages/ProfessionalVariantPreview"),
);
const OnboardingPage = lazy(() => import("@/components/onboarding/SetupIntakePage"));
const OneMoreThingPage = lazy(() => import("@/pages/OneMoreThingPage"));
const SubscribePage = lazy(() => import("@/pages/SubscribePage"));

const Sidebar = lazy(() =>
  import("@/components/layout/Sidebar").then((m) => ({ default: m.Sidebar })),
);
const CommandPalette = lazy(() =>
  import("@/components/CommandPalette").then((m) => ({
    default: m.CommandPalette,
  })),
);
const KeyboardShortcutsOverlay = lazy(() =>
  import("@/components/KeyboardShortcutsOverlay").then((m) => ({
    default: m.KeyboardShortcutsOverlay,
  })),
);
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const ContactProfilePage = lazy(() => import("@/pages/ContactProfilePage"));

const ContactsPage = lazy(() => import("@/pages/ContactsPage"));
const ContactFieldsPage = lazy(() => import("@/pages/ContactFieldsPage"));
const AnalyticsView = lazy(() => import("@/pages/AnalyticsView"));
const GoalsView = lazy(() => import("@/pages/GoalsView"));
const ScheduleView = lazy(() => import("@/pages/ScheduleView"));
const SessionListView = lazy(() => import("@/pages/SessionListView"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const FlowsPage = lazy(() => import("@/pages/FlowsPage"));
const ClientGoalsToolPage = lazy(() => import("@/pages/ClientGoalsToolPage").then((m) => ({ default: m.ClientGoalsToolPage })));
const CustomerDocumentsToolPage = lazy(() => import("@/components/customer-documents/CustomerDocumentsToolPage"));
const CustomerDocumentBuilderPage = lazy(() => import("@/components/customer-documents/CustomerDocumentBuilderPage"));
const MedicalInformationToolPage = lazy(() => import("@/pages/MedicalInformationToolPage"));
const FrontDeskQrFlowPage = lazy(() =>
  import("@/pages/FlowsPage").then((m) => ({ default: m.FrontDeskQrFlowPage })),
);

const TrackableAssetsFlowPage = lazy(() =>
  import("@/pages/FlowsPage").then((m) => ({ default: m.TrackableAssetsFlowPage })),
);

const DigitalReceptionistFlowPage = lazy(() =>
  import("@/pages/FlowsPage").then((m) => ({ default: m.DigitalReceptionistFlowPage })),
);
const ReferralGenerationFlowPage = lazy(() =>
  import("@/pages/ReferralGenerationFlowPage").then((m) => ({ default: m.ReferralGenerationFlowPage })),
);
const TestimonialRequestFlowPage = lazy(() =>
  import("@/pages/TestimonialRequestFlowPage").then((m) => ({ default: m.TestimonialRequestFlowPage })),
);
const AttendanceFlowPage = lazy(() =>
  import("@/pages/FlowsPage").then((m) => ({ default: m.FrontDeskQrFlowPage })),
);
const PastClientReactivationFlowPage = lazy(() =>
  import("@/pages/PastClientReactivationFlowPage").then((m) => ({ default: m.PastClientReactivationFlowPage })),
);
const CaptureLayerView = lazy(() => import("@/pages/CaptureLayerView"));
const WebsiteView = lazy(() => import("@/pages/WebsiteView"));
const WebsiteAreaDetail = lazy(() =>
  import("@/pages/WebsiteView").then((m) => ({ default: m.WebsiteAreaDetail })),
);
const SlideEditorView = lazy(() =>
  import("@/pages/WebsiteView").then((m) => ({ default: m.SlideEditorView })),
);

function RouteFallback() {
  return (
    <div
      className="flex items-center justify-center w-full h-full min-h-[200px]"
      data-testid="route-suspense-fallback"
    >
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );
}

// Shown when the /api/auth/me guard trips the per-IP rate limiter (429).
// Rather than a blank/frozen page, we show a lightweight "signing you in"
// state and auto-retry with a short exponential backoff (2s → 4s → 8s, capped
// at 15s), honoring the server's Retry-After header when present. Retrying is
// safe: a failed 429 doesn't count against the limiter, so the window drains
// while we wait. A manual "Try Now" lets impatient users refetch immediately.
const RATE_LIMIT_BACKOFF_SECONDS = [2, 4, 8] as const;
const RATE_LIMIT_MAX_WAIT_SECONDS = 15;
export function rateLimitWaitSeconds(
  attempt: number,
  retryAfterSeconds?: number,
): number {
  if (retryAfterSeconds && retryAfterSeconds > 0) {
    return Math.min(Math.ceil(retryAfterSeconds), RATE_LIMIT_MAX_WAIT_SECONDS);
  }
  return (
    RATE_LIMIT_BACKOFF_SECONDS[
      Math.min(attempt, RATE_LIMIT_BACKOFF_SECONDS.length - 1)
    ] ?? RATE_LIMIT_MAX_WAIT_SECONDS
  );
}

function RateLimitedFallback({
  onRetry,
  retryAfterSeconds,
}: {
  onRetry: () => void;
  retryAfterSeconds?: number;
}) {
  const [attempt, setAttempt] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(() =>
    rateLimitWaitSeconds(0, retryAfterSeconds),
  );

  useEffect(() => {
    if (secondsLeft <= 0) {
      onRetry();
      const next = attempt + 1;
      setAttempt(next);
      setSecondsLeft(rateLimitWaitSeconds(next, retryAfterSeconds));
      return;
    }
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft, onRetry, attempt, retryAfterSeconds]);

  return (
    <div
      className="flex min-h-screen w-full flex-col items-center justify-center gap-4 bg-background p-6 text-center"
      data-testid="rate-limited-fallback"
    >
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      <div className="max-w-sm space-y-1.5">
        <h1 className="text-lg font-semibold text-foreground">
          Signing You In…
        </h1>
        <p className="text-sm text-muted-foreground">
          This is taking a moment longer than usual. We'll try again
          automatically{secondsLeft > 0 ? ` in ${secondsLeft}s` : " now"}.
        </p>
      </div>
      <button
        type="button"
        onClick={() => {
          onRetry();
          setSecondsLeft(rateLimitWaitSeconds(attempt, retryAfterSeconds));
        }}
        className="rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
        data-testid="button-rate-limit-retry"
      >
        Try Now
      </button>
    </div>
  );
}

// Gate for the admin page — the only surface non-admin users won't have access
// to once multi-user returns. Everything else in the dashboard is available to
// every user. In single-owner mode the owner is always admin, so this renders.
function AdminRoute({ component: Component }: { component: React.ComponentType }) {
  const { authMe, isAdmin, isLoading, isRateLimited, rateLimitRetryAfterSeconds, refetch } =
    useAccessTier();
  if (isRateLimited)
    return (
      <RateLimitedFallback
        onRetry={refetch}
        retryAfterSeconds={rateLimitRetryAfterSeconds}
      />
    );
  if (isLoading) return <RouteFallback />;
  // A View As session keeps the caller's real admin identity for the support
  // banner and Exit endpoint, but the viewed workspace must render exactly
  // like a non-admin account. Do not expose the Admin page/tabs there.
  if (!isAdmin || authMe?.supportSession) return <Redirect to="/dashboard" />;
  return <Component />;
}

function LegacyScheduleRedirect({ tab }: { tab: string }) {
  const [, navigate] = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (isLegacySessionsTab(tab)) {
      navigate(sessionListHref(params.toString()) + window.location.hash, { replace: true });
      return;
    }
    params.set("tab", tab);
    navigate(`/schedule?${params.toString()}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <RouteFallback />;
}

// Task 2700: the standalone Tasks page retired — the task list is now the
// Calendar page's "Task List" tab. Old /tasks bookmarks and deep-links land
// there with their params (add/taskId/filter/folder) preserved. Legacy
// ?view=list / ?view=folders map to view=tasks; calendar views pass through.
function LegacyTasksRedirect() {
  const [, navigate] = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const view = params.get("view");
    if (!view || view === "list" || view === "folders" || view === "tasks") {
      params.set("view", "tasks");
    } else if (view === "calendar") {
      // "calendar" alias → default calendar view.
      params.delete("view");
    }
    params.set("tab", "calendar");
    navigate(`/schedule?${params.toString()}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <RouteFallback />;
}

// Task 2585: Pipeline lives inside Contacts as the board view now. Old
// /pipeline bookmarks and deep-links (including ?stage=/?highlight= params)
// land on the Contacts page with the board view active.
function PipelineRedirect() {
  const [, navigate] = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    params.set("view", "board");
    navigate(`/contacts?${params.toString()}`, { replace: true });
  }, [navigate]);
  return null;
}

function LegacyBillingToolRedirect() {
  const [, navigate] = useLocation();
  useEffect(() => {
    navigate(`/flows/billing${window.location.search}${window.location.hash}`, { replace: true });
  }, [navigate]);
  return <RouteFallback />;
}

function ProtectedRouter() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Switch>
        <Route path="/dashboard" component={Dashboard}/>
        <Route path="/schedule" component={ScheduleView}/>
        <Route path="/sessions" component={SessionListView}/>
        <Route path="/calendar">{() => <LegacyScheduleRedirect tab="calendar" />}</Route>
        <Route path="/tasks" component={LegacyTasksRedirect}/>
        <Route path="/contacts/fields" component={ContactFieldsPage}/>
        <Route path="/contacts/:leadId/documents/:documentId" component={CustomerDocumentBuilderPage}/>
        <Route path="/contacts/:id" component={ContactProfilePage}/>
        <Route path="/contacts" component={ContactsPage}/>
        <Route path="/messaging" component={MessagingRoute}/>
        <Route path="/flows/digital-receptionist" component={DigitalReceptionistFlowPage}/>
        <Route path="/flows/get-pricing">{() => <Redirect to="/website" />}</Route>
        <Route path="/flows/free-consultation">{() => <Redirect to="/website" />}</Route>
        <Route path="/flows/referral-generation" component={ReferralGenerationFlowPage}/>
        <Route path="/flows/testimonial-request" component={TestimonialRequestFlowPage}/>
        <Route path="/flows/rebook-radar">{() => <Redirect to="/flows" replace />}</Route>
        <Route path="/flows/medical-information" component={MedicalInformationToolPage}/>
        <Route path="/flows/client-goals" component={ClientGoalsToolPage}/>
        <Route path="/flows/billing" component={CustomerDocumentsToolPage}/>
        <Route path="/flows/customer-documents" component={LegacyBillingToolRedirect}/>
        <Route path="/flows/attendance" component={AttendanceFlowPage}/>
        <Route path="/flows/past-client-reactivation" component={PastClientReactivationFlowPage}/>
        <Route path="/flows/front-desk-qr" component={FrontDeskQrFlowPage}/>
        <Route path="/flows/trackable-assets" component={TrackableAssetsFlowPage}/>
        <Route path="/flows" component={FlowsPage}/>
        <Route path="/pipeline" component={PipelineRedirect}/>
        <Route path="/analytics" component={AnalyticsView}/>
        <Route path="/goals" component={GoalsView}/>
        <Route path="/classes">{() => <LegacyScheduleRedirect tab="classes" />}</Route>
        <Route path="/admin">{() => <AdminRoute component={AdminDashboard} />}</Route>
        <Route path="/website/campaign/:id" component={CaptureLayerView}/>
        <Route path="/website/pages/:id">{() => <BlogManager />}</Route>
        <Route path="/website/pages">{() => <Redirect to="/website?tab=website&sub=pages" />}</Route>
        <Route path="/website/blog/:id">{() => <BlogManager />}</Route>
        <Route path="/website/blog">{() => <Redirect to="/website?tab=website&sub=blog" />}</Route>
        <Route path="/website/slide/:id" component={SlideEditorView}/>
        <Route path="/website/area/:key" component={WebsiteAreaDetail}/>
        <Route path="/website" component={WebsiteView}/>
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function ScrollToTop({ containerRef }: { containerRef: React.RefObject<HTMLElement | null> }) {
  const [location] = useLocation();
  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash) return;
    containerRef.current?.scrollTo(0, 0);
  }, [location, containerRef]);
  return null;
}

function WindowScrollToTop() {
  const [location] = useLocation();
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      // wait for the destination route to render the anchor
      const tryScroll = (attempt = 0) => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "auto", block: "start" });
        } else if (attempt < 20) {
          setTimeout(() => tryScroll(attempt + 1), 50);
        }
      };
      tryScroll();
      return;
    }
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [location]);
  return null;
}

function RobotsMeta() {
  const [location] = useLocation();
  useEffect(() => {
    // "/" is the public sales page (signed-out visitors); signed-in users are
    // redirected to /dashboard by HomeRoute. It is indexable like the other
    // marketing surfaces and listed in sitemap.xml.
    const isMarketingPage =
      location === "/" ||
      location === "/results" ||
      location === "/privacy" ||
      location === "/terms" ||
      location.startsWith("/p/");
    let meta = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "robots";
      document.head.appendChild(meta);
    }
    meta.content = isMarketingPage ? "index, follow" : "noindex, nofollow";
  }, [location]);
  return null;
}

// Admin "View as user" support session banner (Task 2057): persistent across
// every dashboard page while a session is active. Exit ends the session
// server-side, then hard-reloads back to the Admin page so no cached query
// data from the subscriber's account survives. Text sizes are absolute px —
// the dashboard root font-size is 20px, so rem classes inflate.
function SupportSessionBanner() {
  const { authMe } = useAccessTier();
  const [exiting, setExiting] = useState(false);
  const session = authMe?.supportSession;
  if (!session) return null;
  const who = session.targetName || session.targetEmail || session.targetUserId;
  return (
    <div
      className="flex items-center justify-center gap-3 bg-amber-500 text-black px-4 py-2 shrink-0 z-50"
      data-testid="banner-support-session"
    >
      <span className="text-[14px] font-medium truncate">
        Viewing {who}
        {session.targetEmail && session.targetName ? ` (${session.targetEmail})` : ""}'s account for support
      </span>
      <button
        type="button"
        disabled={exiting}
        onClick={async () => {
          setExiting(true);
          try {
            await api.admin.endSupportSession();
          } finally {
            window.location.assign("/admin?tab=users");
          }
        }}
        className="text-[13px] font-semibold border border-black/40 rounded px-2.5 py-0.5 hover:bg-black/10 disabled:opacity-60"
        data-testid="button-exit-support-session"
      >
        {exiting ? "Exiting…" : "Exit"}
      </button>
    </div>
  );
}

function AppShell() {
  const { theme } = useTheme();
  const { authMe } = useAccessTier();
  const settingsQuery = useQuery({
    queryKey: ["settings"],
    queryFn: () => api.settings.get(),
    staleTime: 30_000,
  });
  const gridEnabled = theme.effects.gridEnabled;
  const mainRef = useRef<HTMLElement>(null);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const { open: shortcutsOpen, close: closeShortcuts } = useShortcutsOverlay();

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      const isModK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k";
      if (!isModK) return;
      const target = e.target as HTMLElement | null;
      const inEditable =
        !!target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.tagName === "SELECT" ||
          (target as HTMLElement).isContentEditable);
      const insidePalette = !!target?.closest('[data-testid="command-palette-overlay"]');
      if (inEditable && !insidePalette) return;
      e.preventDefault();
      setPaletteOpen(true);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Onboarding gate: until the user finishes the wizard, route them there.
  // While settings load, show the route fallback to avoid a flash.
  if (settingsQuery.isLoading) return <RouteFallback />;
  if (settingsQuery.data && settingsQuery.data.onboardingCompleted === false && !authMe?.supportSession) {
    return <Redirect to="/onboarding" />;
  }
  // The post-onboarding "Why did you join?" answer is required before the
  // dashboard shell opens. Existing accounts that never answered are routed to
  // the same full-screen question once; submitting it invalidates this query.
  const purchaseReason = settingsQuery.data?.purchaseReason;
  if (
    !E2E_AUTH_BYPASS &&
    !authMe?.supportSession &&
    settingsQuery.data?.onboardingCompleted === true &&
    !settingsQuery.data?.onboardingIntake?.readyAt &&
    purchaseReason !== undefined &&
    (purchaseReason === null || !purchaseReason.trim())
  ) {
    return <Redirect to="/one-more-thing" />;
  }

  return (
    <div className="flex h-screen w-full flex-col bg-background overflow-hidden text-foreground">
      <SupportSessionBanner />
      <div className="flex flex-1 min-h-0 w-full relative">
      {gridEnabled && (
        <div className="absolute inset-0 pointer-events-none opacity-20 z-0" 
             style={{
               backgroundImage: `
                 linear-gradient(rgba(${theme.effects.gridColor}, 0.1) 1px, transparent 1px),
                 linear-gradient(90deg, rgba(${theme.effects.gridColor}, 0.1) 1px, transparent 1px)
               `,
               backgroundSize: '40px 40px',
               backgroundPosition: 'center center'
             }}>
        </div>
      )}
      
      <Suspense fallback={null}>
        <Sidebar onOpenSearch={() => setPaletteOpen(true)} />
      </Suspense>
      <main ref={mainRef} className="flex-1 h-full overflow-y-auto relative z-10 custom-scrollbar pb-24 md:pb-0">
        <ScrollToTop containerRef={mainRef} />
        <ProtectedRouter />
      </main>
      <Suspense fallback={null}>
        <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      </Suspense>
      <Suspense fallback={null}>
        <KeyboardShortcutsOverlay open={shortcutsOpen} onClose={closeShortcuts} />
      </Suspense>
      <SettingsHub />
      </div>
    </div>
  );
}

function RedirectToDashboard() {
  return <Redirect to="/dashboard" />;
}

// Wires the Clerk session into the API client (Bearer token) and, once signed
// in, pings /api/auth/init so the server seeds the user's defaults on first
// login. Renders nothing.
function AuthInit() {
  const { isSignedIn, getToken } = useAppAuth();
  useEffect(() => installEffectiveWorkspaceIsolation(queryClient), []);
  useEffect(() => {
    setClerkGetToken(() => getToken());
    return () => setClerkGetToken(null);
  }, [getToken]);
  useEffect(() => {
    if (!isSignedIn) return;
    (async () => {
      try {
        const token = await getToken();
        await fetch("/api/auth/init", {
          method: "POST",
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
      } catch (err) {
        console.error("[auth] init failed", err);
      }
    })();
  }, [isSignedIn, getToken]);
  return null;
}

function TenantIsolationBoundary({ children }: { children: React.ReactNode }) {
  const { isLoaded, isSignedIn, userId } = useAppAuth();
  const identity = isSignedIn ? userId : "__signed_out__";
  const [activeIdentity, setActiveIdentity] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoaded || !identity) return;
    isolateBrowserForIdentity(queryClient, isSignedIn ? userId : null);
    setActiveIdentity(identity);
  }, [identity, isLoaded, isSignedIn, userId]);

  if (!isLoaded || !identity || activeIdentity !== identity) {
    return <RouteFallback />;
  }

  return <div key={identity} className="contents">{children}</div>;
}

// Hosted sign-in / sign-up screens (Clerk's prebuilt component, hash routing so
// a single wouter route handles every sub-step).
function AuthScreen({ mode }: { mode: "sign-in" | "sign-up" }) {
  const safeRedirect = safePostAuthRedirect(window.location.search);
  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-background p-4">
      {mode === "sign-in" ? (
        <SignIn routing="hash" signUpUrl={`/sign-up?redirect_url=${encodeURIComponent(safeRedirect)}`} forceRedirectUrl={safeRedirect} />
      ) : (
        <SignUp routing="hash" signInUrl={`/sign-in?redirect_url=${encodeURIComponent(safeRedirect)}`} forceRedirectUrl={safeRedirect} />
      )}
    </div>
  );
}

// The signed-in application (paid-access gate + onboarding gate + dashboard
// chrome). Mounted only inside <ProtectedArea>, so every route here is
// guaranteed an authenticated user.
// Paid-access gate. Runs after sign-in, before onboarding/dashboard. When
// billing is configured and the user has no active subscription (and isn't
// admin), every route funnels to /subscribe; once active, /subscribe bounces
// back into the app. When billing isn't configured the gate is dormant
// (subscriptionActive defaults true), so this is a no-op — mirroring the
// server, which only enforces 402 when Stripe is connected.
function AuthenticatedApp() {
  const {
    isLoading,
    isRateLimited,
    rateLimitRetryAfterSeconds,
    refetch,
    isAdmin,
    subscriptionActive,
  } = useAccessTier();
  const [location] = useLocation();
  // Purchase-first: direct/legacy signups must subscribe before onboarding.
  const needsSubscription = !isLoading && !isAdmin && !subscriptionActive;

  if (isRateLimited)
    return (
      <RateLimitedFallback
        onRetry={refetch}
        retryAfterSeconds={rateLimitRetryAfterSeconds}
      />
    );
  if (isLoading) return <RouteFallback />;

  if (needsSubscription) {
    if (location !== "/subscribe") {
      return <Redirect to="/subscribe" />;
    }
  }
  if (!needsSubscription && location === "/subscribe") {
    return <Redirect to="/" />;
  }

  return (
    <Suspense fallback={<RouteFallback />}>
      <Switch>
        <Route path="/subscribe" component={SubscribePage} />
        <Route path="/onboarding">
          {() => (
            <div className="flex min-h-screen w-full flex-col bg-background">
              <SupportSessionBanner />
              <AccentColorSync />
              <div className="min-h-0 flex-1">
                <OnboardingPage />
              </div>
            </div>
          )}
        </Route>
        {/* "1 More Thing" — full-screen post-onboarding question page (no
            sidebar shell), reached from the setup interstitial's button. */}
        <Route path="/one-more-thing">
          {() => (
            <div className="flex min-h-screen w-full flex-col bg-background">
              <SupportSessionBanner />
              <AccentColorSync />
              <div className="min-h-0 flex-1">
                <OneMoreThingPage />
              </div>
            </div>
          )}
        </Route>
        <Route path="/" component={RedirectToDashboard} />
        <Route path="/sign-in" component={RedirectToDashboard} />
        <Route path="/sign-up" component={RedirectToDashboard} />
        <Route>
          <AccentColorSync />
          <SettingsHubProvider>
            <AppShell />
          </SettingsHubProvider>
        </Route>
      </Switch>
    </Suspense>
  );
}

// "/" in the SPA. In production the server serves the owner's public website
// (the Vault template in sales mode) at the root BEFORE the SPA catch-all, so
// this route only renders when that page couldn't be resolved (dev without
// owner data, transient failures) or after client-side navigation to "/".
// Signed-in users go into the app; signed-out visitors get a minimal branded
// fallback with the same Sign Up / Log In actions the sales header offers.
export function HomeRoute() {
  const { isLoaded, isSignedIn } = useAppAuth();
  if (!isLoaded) return <RouteFallback />;
  if (isSignedIn) return <Redirect to="/dashboard" />;
  // Temporary holding page — shown whenever the owner's sales page can't be
  // served at "/" (e.g. right after a full account reset, before the website
  // is rebuilt in onboarding). Keep this fallback aligned with the dashboard,
  // rather than inheriting the public sales-page branding.
  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <main className={`${PAGE_FRAME} flex flex-1 flex-col items-center justify-center text-center`}>
        <div className="flex w-full max-w-md flex-col items-center gap-6">
          <span
            className="ds-badge border border-border bg-muted text-muted-foreground"
            data-testid="badge-home-status"
          >
          We're Making Some Updates
          </span>
          <h1 className="flex justify-center" data-testid="text-home-title">
            <img
              src="/favicon-512.png"
              alt="InControl"
              className="h-24 w-24 max-w-full object-contain sm:h-32 sm:w-32"
              data-testid="image-home-logo"
            />
          </h1>
          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
            <a
              href="/sign-in"
              onClick={() => trackEvent("sales_account_login_cta", { placement: "fallback_home" })}
              data-testid="link-home-login"
              className="ds-btn-secondary w-full no-underline focus-visible:outline-2 focus-visible:outline-foreground focus-visible:outline-offset-2 sm:w-auto"
            >
              Log In
            </a>
            <a
              href="/sign-up"
              onClick={() => trackEvent("sales_account_signup_cta", { placement: "fallback_home" })}
              data-testid="link-home-signup"
              className="ds-btn-primary w-full no-underline focus-visible:outline-2 focus-visible:outline-foreground focus-visible:outline-offset-2 sm:w-auto"
            >
              Sign Up
            </a>
          </div>
        </div>
      </main>
      <footer className="border-t border-border px-6 py-5 text-center">
        <p className="ds-meta">
          © {new Date().getFullYear()} InControl ·{" "}
          <a href="/privacy" className="underline underline-offset-2 hover:text-foreground">
            Privacy
          </a>{" "}
          ·{" "}
          <a href="/terms" className="underline underline-offset-2 hover:text-foreground">
            Terms
          </a>
        </p>
      </footer>
    </div>
  );
}

// Auth gate for the application area: blocks until Clerk has loaded, then sends
// logged-out visitors to sign-in and signed-in users into the app.
function ProtectedArea() {
  const { isLoaded, isSignedIn } = useAppAuth();
  if (!isLoaded) return <RouteFallback />;
  if (!isSignedIn) {
    const destination =
      `${window.location.pathname}${window.location.search}${window.location.hash}`;
    return <Redirect to={signInUrlForLocation(destination)} />;
  }
  return (
    <TenantIsolationBoundary>
      <AuthenticatedApp />
    </TenantIsolationBoundary>
  );
}

// Paths that belong to the authenticated app (gated by <ProtectedArea>). Kept in
// sync with the routes in <AuthenticatedApp> + <AppShell>'s ProtectedRouter. Any
// path NOT listed here is a genuine unknown URL and renders the branded 404 —
// including for logged-out visitors, who would otherwise be bounced to /sign-in.
// Public Switch catch-all: send known app paths through the auth gate, render the
// custom 404 for everything else (so unknown public URLs don't redirect to login).
function PublicCatchAll() {
  const [location] = useLocation();
  // Exact-match the known app paths, plus the dynamic area/media detail
  // sub-routes, reachable only via the Marketing page's Library/Media grids.
  if (isApplicationRoutePath(location))
    return <ProtectedArea />;
  return <NotFound />;
}

// Multi-user, Clerk-gated workspace. Public surfaces (marketing, legal, and the
// hosted Vault page) render without login; the sign-in/up screens are public
// too; everything else requires an authenticated session.
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <ThemeProvider>
          <RobotsMeta />
          <WindowScrollToTop />
          <AuthInit />
          <Suspense fallback={<RouteFallback />}>
            <Switch>
              {/* Public surfaces — no authentication required. */}
              <Route path="/" component={HomeRoute} />
              <Route path="/results" component={ResultsPage} />
              <Route path="/privacy" component={PrivacyPage} />
              <Route path="/terms" component={TermsPage} />
              <Route path="/vault-template-preview">{() => <VaultTemplatePage />}</Route>
              {/* Dev-only harness for the LeadCaptureSettings container-query
                  reflow e2e (script/e2e-lead-capture-columns.ts). Never shipped
                  in a production build. */}
              {import.meta.env.DEV && (
                <Route path="/lead-capture-preview">{() => <LeadCapturePreview />}</Route>
              )}
              {/* Dev-only harness for the ONBOARDING Contact-step lead-capture
                  reflow e2e (script/e2e-onboarding-lead-capture.ts). Never
                  shipped in a production build. */}
              {import.meta.env.DEV && (
                <Route path="/onboarding-lead-capture-preview">
                  {() => <OnboardingLeadCapturePreview />}
                </Route>
              )}
              {/* Dev-only harness for the WEBSITE → Contact form lead-capture
                  reflow e2e (script/e2e-website-contact-lead-capture.ts). Never
                  shipped in a production build. */}
              {import.meta.env.DEV && (
                <Route path="/website-contact-lead-capture-preview">
                  {() => <WebsiteContactLeadCapturePreview />}
                </Route>
              )}
              {/* Dev-only harness for the VAULT campaign editor lead-capture
                  reflow e2e (script/e2e-vault-campaign-lead-capture.ts). Never
                  shipped in a production build. */}
              {import.meta.env.DEV && (
                <Route path="/vault-campaign-lead-capture-preview">
                  {() => <VaultCampaignLeadCapturePreview />}
                </Route>
              )}
              {/* Dev-only harness for the professional-background picker render
                  e2e (script/e2e-professional-variant.ts). Never shipped in a
                  production build. */}
              {import.meta.env.DEV && (
                <Route path="/professional-variant-preview">
                  {() => <ProfessionalVariantPreview />}
                </Route>
              )}
              {/* Digital Receptionist iframe embed for the server-rendered
                  legal pages (/p/<slug>/terms|privacy) — renders ONLY the chat
                  widget on a transparent background. Must precede /p/:slug. */}
              <Route path="/p/:slug/receptionist-embed">
                {(params) => <ReceptionistEmbedPage slug={params.slug} />}
              </Route>
              <Route path="/p/:slug">{(params) => <VaultTemplatePage slug={params.slug} />}</Route>

              {/* Auth screens. */}
              <Route path="/purchase-complete" component={PurchaseCompletePage} />
              <Route path="/sign-in">{() => <AuthScreen mode="sign-in" />}</Route>
              <Route path="/sign-up">{() => <AuthScreen mode="sign-up" />}</Route>

              {/* Known app routes go through the auth gate; any other URL is a
                  genuine 404 (rendered for logged-out visitors too). */}
              <Route>
                <PublicCatchAll />
              </Route>
            </Switch>
          </Suspense>
          <Toaster />
        </ThemeProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
